import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Rating,
    Select,
    TextField,
    Typography
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import MusicVideoIcon from '@mui/icons-material/MusicVideo';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ImageIcon from '@mui/icons-material/Image';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import logo from '../../images/logo_safedesk.png';
import { getUploadUrl } from '../../service/ServiceApi';
import SurveyApi, { SurveyAttachment, SurveyTaskInfo } from '../../service/SurveyApi';

type Status = 'loading' | 'invalid' | 'ready' | 'submitting' | 'success' | 'already_responded';

const surveyApi = new SurveyApi();

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const PDF_EXTENSIONS = ['pdf'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'flac', 'm4a'];
const TEXT_EXTENSIONS = ['txt', 'log', 'csv', 'json', 'xml', 'md'];

function getExtension(name: string): string {
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.substring(idx + 1).toLowerCase() : '';
}

function isImage(name: string): boolean {
    return IMAGE_EXTENSIONS.includes(getExtension(name));
}

function getFileIcon(name: string) {
    const ext = getExtension(name);
    const sx = { fontSize: 32, color: '#555' };
    if (IMAGE_EXTENSIONS.includes(ext)) return <ImageIcon sx={sx} />;
    if (PDF_EXTENSIONS.includes(ext)) return <PictureAsPdfIcon sx={{ ...sx, color: '#d32f2f' }} />;
    if (VIDEO_EXTENSIONS.includes(ext)) return <OndemandVideoIcon sx={sx} />;
    if (AUDIO_EXTENSIONS.includes(ext)) return <MusicVideoIcon sx={sx} />;
    if (TEXT_EXTENSIONS.includes(ext)) return <TextSnippetIcon sx={sx} />;
    return <InsertDriveFileIcon sx={sx} />;
}

function AttachmentItem({ attachment }: { attachment: SurveyAttachment }) {
    const url = `${getUploadUrl()}${attachment.dir}`;
    const image = isImage(attachment.name);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            padding: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#fff'
        }}>
            <Box sx={{
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0
            }}>
                {image ? (
                    <a href={url} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                        <img
                            src={url}
                            alt={attachment.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </a>
                ) : getFileIcon(attachment.name)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Link
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={attachment.name}
                >
                    {attachment.name}
                </Link>
            </Box>
        </Box>
    );
}

export default function SurveyPage() {
    const { token } = useParams<{ token: string }>();

    const [status, setStatus] = useState<Status>('loading');
    const [info, setInfo] = useState<SurveyTaskInfo | null>(null);
    const [solutionRating, setSolutionRating] = useState<number | null>(null);
    const [solutionReason, setSolutionReason] = useState<string>('');
    const [serviceRating, setServiceRating] = useState<number | null>(null);
    const [serviceReason, setServiceReason] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const data = await surveyApi.getByToken(token);
                if (cancelled) return;
                setInfo(data);
                if (data.already_responded) {
                    setStatus('already_responded');
                } else {
                    setStatus('ready');
                }
            } catch {
                if (!cancelled) setStatus('invalid');
            }
        }
        load();
        return () => { cancelled = true; };
    }, [token]);

    function isReadyToSubmit(): boolean {
        return solutionRating !== null && solutionRating >= 1
            && serviceRating !== null && serviceRating >= 1
            && email.trim() !== '';
    }

    async function handleSubmit() {
        if (!isReadyToSubmit() || solutionRating === null || serviceRating === null) return;
        setStatus('submitting');
        setErrorMessage('');
        try {
            await surveyApi.submit(token, {
                solution_rating: solutionRating,
                solution_reason: solutionRating < 5 && solutionReason.trim() ? solutionReason.trim() : null,
                service_rating: serviceRating,
                service_reason: serviceRating < 5 && serviceReason.trim() ? serviceReason.trim() : null,
                email: email.trim()
            });
            setStatus('success');
        } catch (e: any) {
            const message: string = e?.message || '';
            if (message.toLowerCase().includes('already')) {
                setStatus('already_responded');
            } else {
                setErrorMessage('Não foi possível enviar sua resposta. Tente novamente.');
                setStatus('ready');
            }
        }
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            backgroundColor: '#f5f5f5',
            padding: { xs: 2, sm: 4 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start'
        }}>
            <Paper elevation={2} sx={{ maxWidth: 720, width: '100%', padding: { xs: 3, sm: 4 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img src={logo} alt="SafeDesk" style={{ maxWidth: 260, width: '100%', height: 'auto' }} />
                </Box>

                {status === 'loading' && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {status === 'invalid' && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="error">Link inválido ou expirado.</Typography>
                    </Box>
                )}

                {status === 'already_responded' && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6">Esta pesquisa já foi respondida.</Typography>
                    </Box>
                )}

                {status === 'success' && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>Obrigado pelo seu feedback!</Typography>
                        <Typography>Sua resposta foi registrada com sucesso.</Typography>
                    </Box>
                )}

                {(status === 'ready' || status === 'submitting') && info && (
                    <>
                        <Typography variant="h5" sx={{ mb: 3 }}>Pesquisa de satisfação</Typography>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="textSecondary">Número do chamado</Typography>
                            <Typography>{info.ticket_code}</Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="textSecondary">Assunto</Typography>
                            <Typography>{info.name}</Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="textSecondary">Solução aplicada</Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{info.solution || '-'}</Typography>
                        </Box>

                        {info.attachments_solution.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                                    Anexos da solução
                                </Typography>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                                    gap: 1
                                }}>
                                    {info.attachments_solution.map(a => (
                                        <AttachmentItem key={a.id} attachment={a} />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ mb: 1 }}>Qual a sua nota para a solução?</Typography>
                            <Rating
                                value={solutionRating}
                                onChange={(_, v) => setSolutionRating(v)}
                                size="large"
                            />
                            {solutionRating !== null && solutionRating < 5 && (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Motivo (opcional)"
                                    value={solutionReason}
                                    onChange={e => setSolutionReason(e.target.value)}
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ mb: 1 }}>Qual a sua nota para o atendimento?</Typography>
                            <Rating
                                value={serviceRating}
                                onChange={(_, v) => setServiceRating(v)}
                                size="large"
                            />
                            {serviceRating !== null && serviceRating < 5 && (
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Motivo (opcional)"
                                    value={serviceReason}
                                    onChange={e => setServiceReason(e.target.value)}
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <FormControl fullWidth required>
                                <InputLabel id="survey-email-label">Selecione seu e-mail</InputLabel>
                                <Select
                                    labelId="survey-email-label"
                                    label="Selecione seu e-mail"
                                    value={email}
                                    onChange={e => setEmail(String(e.target.value))}
                                >
                                    {info.customer_contacts.map(c => (
                                        <MenuItem key={c.id} value={c.email}>
                                            {c.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {errorMessage && (
                            <Typography color="error" sx={{ mb: 2 }}>{errorMessage}</Typography>
                        )}

                        <Box sx={{ textAlign: 'right' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!isReadyToSubmit() || status === 'submitting'}
                                onClick={handleSubmit}
                            >
                                {status === 'submitting' ? <CircularProgress size={20} color="inherit" /> : 'Enviar'}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
}
